import axios from "axios";
import { base_url, config } from "../../utils/axiosConfig";

const register = async (userData) => {
  try {
    // Sanitize input data to prevent injection attacks
    const sanitizedData = {
      firstname: userData.firstname?.trim(),
      lastname: userData.lastname?.trim(),
      email: userData.email?.trim().toLowerCase(),
      mobile: userData.mobile?.trim(),
      password: userData.password
    };
    
    const response = await axios.post(`${base_url}user/register`, sanitizedData);
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // Re-throw the error so it can be handled by the calling function
    throw error;
  }
};

// Register user and transfer guest orders
const registerWithOrderTransfer = async (userData) => {
  try {
    // Sanitize input data to prevent injection attacks
    const sanitizedData = {
      firstname: userData.firstname?.trim(),
      lastname: userData.lastname?.trim(),
      email: userData.email?.trim().toLowerCase(),
      mobile: userData.mobile?.trim(),
      password: userData.password,
      // Include guest user ID if it exists
      guestUserId: localStorage.getItem("guestUserId") || null
    };
    
    const response = await axios.post(`${base_url}user/register-with-orders`, sanitizedData);
    if (response.data) {
      // Clear guest user ID from localStorage after successful registration
      localStorage.removeItem("guestUserId");
      return response.data;
    }
  } catch (error) {
    // Re-throw the error so it can be handled by the calling function
    throw error;
  }
};

// Create guest user
const createGuestUser = async (guestData) => {
  try {
    const response = await axios.post(`${base_url}user/guest-register`, guestData);
    if (response.data) {
      return response.data;
    } else {
      throw new Error("Empty response from server");
    }
  } catch (error) {
    // Re-throw the error so it can be handled by the calling function
    throw error;
  }
};

const login = async (userData) => {
  try {
    // Sanitize input to prevent injection attacks
    const sanitizedData = {
      email: userData.email?.trim().toLowerCase(),
      password: userData.password,
      recaptchaToken: userData.recaptchaToken // Include reCAPTCHA token
    };
    
    const response = await axios.post(`${base_url}user/login`, sanitizedData);

    return response.data;
  } catch (error) {
    // Re-throw the error so it can be handled by the calling function
    throw error;
  }
};

// Google login function
const googleLogin = async (userData) => {
  try {
    // For Google login, we're just storing the user data received from the backend
    // The backend has already authenticated the user with Google
    return userData;
  } catch (error) {
    // Re-throw the error so it can be handled by the calling function
    throw error;
  }
};

const getUserWishlist = async (dynamicConfig) => {
  try {
    const response = await axios.get(`${base_url}user/wishlist`, dynamicConfig);
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } 
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    // Also handle 500 errors that might contain token expiration messages
    else if (error.response && error.response.status === 500) {
      if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
    }
    throw error;
  }
};

// Add to wishlist for guest users
const addToWishlistGuest = async (prodId) => {
  // For guest users, we'll store wishlist in localStorage
  const guestWishlist = JSON.parse(localStorage.getItem("guestWishlist")) || [];
  const isAlreadyInWishlist = guestWishlist.includes(prodId);
  
  if (isAlreadyInWishlist) {
    // Remove from wishlist
    const updatedWishlist = guestWishlist.filter(id => id !== prodId);
    localStorage.setItem("guestWishlist", JSON.stringify(updatedWishlist));
    return { message: "Product removed from wishlist" };
  } else {
    // Add to wishlist
    guestWishlist.push(prodId);
    localStorage.setItem("guestWishlist", JSON.stringify(guestWishlist));
    return { message: "Product added to wishlist" };
  }
};

const addToCart = async (cartData, dynamicConfig) => {
  try {
    const response = await axios.post(`${base_url}user/cart`, cartData, dynamicConfig);
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    // Also handle 500 errors that might contain token expiration messages
    else if (error.response && error.response.status === 500) {
      if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
    }
    throw error;
  }
};

// Add to cart for guest users
const addToCartGuest = async (cartData) => {
  // For guest users, we'll store cart items in localStorage
  let guestCart = JSON.parse(localStorage.getItem("guestCart")) || [];
  
  // Check if product already exists in cart
  const existingItemIndex = guestCart.findIndex(item => item.productId === cartData.productId);
  
  if (existingItemIndex !== -1) {
    // Update quantity if product already exists
    guestCart[existingItemIndex].quantity = cartData.quantity;
  } else {
    // Add new item to cart
    guestCart.push({
      productId: cartData.productId,
      quantity: cartData.quantity,
      price: cartData.price,
      isPreorder: cartData.isPreorder || false,
      preorderDeposit: cartData.preorderDeposit || 0,
      // Add a temporary ID for identification
      tempId: Date.now() + Math.random()
    });
  }
  
  // Save updated cart to localStorage
  localStorage.setItem("guestCart", JSON.stringify(guestCart));
  
  // Return the added item
  const addedItem = existingItemIndex !== -1 ? guestCart[existingItemIndex] : guestCart[guestCart.length - 1];
  return addedItem;
};

const getCart = async (dynamicConfig) => {
  try {
    const response = await axios.get(`${base_url}user/cart`, dynamicConfig);
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

// Get cart for guest users
const getGuestCart = async () => {
  // For guest users, we'll retrieve cart items from localStorage
  let guestCart = JSON.parse(localStorage.getItem("guestCart")) || [];
  
  // Fetch product details for each item in the guest cart
  const guestCartWithDetails = await Promise.all(
    guestCart.map(async (item) => {
      try {
        const response = await axios.get(`${base_url}product/${item.productId}`);
        return {
          ...item,
          productDetails: response.data
        };
      } catch (error) {
        // If there's an error fetching product details, return the item as is
        console.error("Error fetching product details for guest cart item:", error);
        return item;
      }
    })
  );
  
  return guestCartWithDetails;
};

const removeProductFromCart = async (data) => {
  try {
    const response = await axios.delete(
      `${base_url}user/delete-product-cart/${data.id}`,
      data.config
    );
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

// Remove product from guest cart
const removeProductFromGuestCart = async (cartItemId) => {
  // For guest users, we'll remove cart items from localStorage
  let guestCart = JSON.parse(localStorage.getItem("guestCart")) || [];
  
  // Filter out the item to be removed
  const updatedCart = guestCart.filter(item => item.tempId !== cartItemId);
  
  // Save updated cart to localStorage
  localStorage.setItem("guestCart", JSON.stringify(updatedCart));
  
  return { message: "Product removed from cart" };
};

// Update product quantity in guest cart
const updateProductQuantityFromGuestCart = async (cartItemId, newQuantity) => {
  // For guest users, we'll update cart items in localStorage
  let guestCart = JSON.parse(localStorage.getItem("guestCart")) || [];
  
  // Find the item to update
  const itemIndex = guestCart.findIndex(item => item.tempId === cartItemId);
  
  if (itemIndex !== -1) {
    // Update the quantity
    guestCart[itemIndex].quantity = newQuantity;
    
    // Save updated cart to localStorage
    localStorage.setItem("guestCart", JSON.stringify(guestCart));
    
    return guestCart[itemIndex];
  }
  
  return null;
};

const updateProductFromCart = async (cartDetail, dynamicConfig) => {
  try {
    const response = await axios.put(
      `${base_url}user/update-product-cart/${cartDetail.cartItemId}/${cartDetail.quantity}`,
      {},
      dynamicConfig
    );
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const createOrder = async (orderDetail, dynamicConfig) => {
  try {
    const response = await axios.post(
      `${base_url}user/cart/create-order/`,
      orderDetail,
      dynamicConfig
    );
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

// Create order for guest user
const createGuestOrder = async (orderDetail) => {
  // Check if guest user ID is provided in orderDetail
  if (!orderDetail.guestUserId) {
    throw new Error("Guest user ID is required for guest orders");
  }
  
  // Use the provided guest user ID
  const guestUserId = orderDetail.guestUserId;
  
  // Use the orderItems directly from orderDetail instead of localStorage
  // This is important for instant checkout where we don't use localStorage cart
  const orderItems = orderDetail.orderItems;
  
  // Validate order items
  if (!orderItems || !Array.isArray(orderItems) || orderItems.length === 0) {
    throw new Error("Order items are required and must be a non-empty array");
  }
  
  // Validate each order item has required fields
  for (let i = 0; i < orderItems.length; i++) {
    const item = orderItems[i];
    if (!item.product) {
      throw new Error(`Order item at index ${i} is missing the required 'product' field`);
    }
    if (item.quantity === undefined || item.quantity === null) {
      throw new Error(`Order item at index ${i} is missing the required 'quantity' field`);
    }
    if (item.price === undefined || item.price === null) {
      throw new Error(`Order item at index ${i} is missing the required 'price' field`);
    }
  }
  
  // Include guestUserId in the order data sent to the backend
  const orderData = {
    ...orderDetail,
    orderItems // Use the formatted order items
  };
  
  console.log("Sending guest order data to backend:", orderData);
  
  const response = await axios.post(
    `${base_url}user/guest-cart/create-order/`,
    orderData
  );
  
  if (response.data) {
    return response.data;
  }
};

const updatePreorderPayment = async (orderId, paymentInfo, dynamicConfig) => {
  try {
    const response = await axios.put(
      `${base_url}user/update-preorder-payment/${orderId}`,
      { paymentInfo },
      dynamicConfig
    );
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const getUserOrders = async (dynamicConfig) => {
  try {
    const response = await axios.get(`${base_url}user/getmyorders`, dynamicConfig);

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const getSingleUserOrder = async (orderId, dynamicConfig) => {
  try {
    const response = await axios.get(`${base_url}user/order/${orderId}`, dynamicConfig);

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const updateUser = async (data) => {
  try {
    const response = await axios.put(
      `${base_url}user/edit-user`,
      data.data,
      data.config
    );

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

// Address management functions
const saveUserAddress = async (addressData, dynamicConfig) => {
  try {
    const response = await axios.put(
      `${base_url}user/save-address`,
      { address: addressData },
      dynamicConfig
    );

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const getUserAddresses = async (dynamicConfig) => {
  try {
    const response = await axios.get(
      `${base_url}user/addresses`,
      dynamicConfig
    );

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const updateUserAddress = async (addressId, addressData, dynamicConfig) => {
  try {
    const response = await axios.put(
      `${base_url}user/addresses/${addressId}`,
      { address: addressData },
      dynamicConfig
    );

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const deleteUserAddress = async (addressId, dynamicConfig) => {
  try {
    const response = await axios.delete(
      `${base_url}user/addresses/${addressId}`,
      dynamicConfig
    );

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const setDefaultAddress = async (addressId, dynamicConfig) => {
  try {
    const response = await axios.put(
      `${base_url}user/addresses/${addressId}/default`,
      {},
      dynamicConfig
    );

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

const forgotPasswordToken = async (data) => {
  const response = await axios.post(
    `${base_url}user/forgot-password-token`,
    data
  );

  if (response.data) {
    return response.data;
  }
};

const resetPass = async (data) => {
  const response = await axios.put(
    `${base_url}user/reset-password/${data.token}`,
    {
      password: data?.password,
    }
  );

  if (response.data) {
    return response.data;
  }
};

const resendVerificationEmail = async (data) => {
  const response = await axios.post(
    `${base_url}user/resend-verification-email`,
    data
  );

  if (response.data) {
    return response.data;
  }
};

const emptyCart = async (dynamicConfig) => {
  try {
    const response = await axios.delete(`${base_url}user/empty-cart`, dynamicConfig);

    if (response.data) {
      return response.data;
    }
  } catch (error) {
    // If it's an authentication error, handle appropriately
    if (error.response && error.response.status === 401) {
      // Check if it's a session invalidation error
      if (error.response.data.message && error.response.data.message.includes("Session invalidated")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      }
      // Check if it's a token expiration error
      else if (error.response.data.message && error.response.data.message.includes("Access token expired")) {
        // Don't redirect immediately, let the slice handle the error message
        throw error;
      } else {
        // For other 401 errors, redirect to login
        localStorage.removeItem("customer");
        window.location.href = "/login";
      }
    }
    throw error;
  }
};

export const authService = {
  register,
  registerWithOrderTransfer,
  createGuestUser,
  login,
  googleLogin,
  getUserWishlist,
  addToCart,
  getCart,
  removeProductFromCart,
  updateProductFromCart,
  createOrder,
  updatePreorderPayment, // Add this new function
  getUserOrders,
  getSingleUserOrder,
  updateUser,
  saveUserAddress,
  getUserAddresses,
  updateUserAddress,
  deleteUserAddress,
  setDefaultAddress,
  forgotPasswordToken,
  resetPass,
  emptyCart,
  resendVerificationEmail,
  // Guest user functions
  addToCartGuest,
  getGuestCart,
  removeProductFromGuestCart,
  updateProductQuantityFromGuestCart,
  createGuestOrder,
  addToWishlistGuest,
};